import { AiImageInput } from "@/services/fobizzToolsApi";

const PAGINATION_LIMIT = 10;

export function useImageQueries() {
  const queryClient = useQueryClient();
  const { aiImages } = useApiClient();
  const isLoading = useIsFetching({ queryKey: ["images"] });

  const useInfiniteIndexQuery = ({
    page,
    limit,
  }: {
    page?: Ref<number>;
    limit?: number;
  }) => {
    return useInfiniteQuery(
      {
        queryKey: ["images", "infinite", page],
        queryFn: ({ pageParam }) => {
          return aiImages.listAiImages({
            page: pageParam,
            limit: limit || PAGINATION_LIMIT,
          });
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, _pages) => lastPage.nextPage,
      },
      queryClient
    );
  };

  const useIndexQuery = ({
    page,
    limit,
  }: {
    page?: Ref<number>;
    limit?: number;
  }) => {
    return useQuery(
      {
        queryKey: ["images", page],
        queryFn: () => {
          return aiImages.listAiImages({
            page: (page && page.value) || 1,
            limit: limit || PAGINATION_LIMIT,
          });
        },
        placeholderData: keepPreviousData,
      },
      queryClient
    );
  };

  const getQuery = (id: Ref<string>, opts = {}) => {
    return useQuery(
      {
        queryKey: ["images", id],
        queryFn: () => aiImages.getAiImage({ id: id.value }),
        ...opts,
      },
      queryClient
    );
  };

  const useDeleteMutation = () => {
    return useMutation(
      {
        mutationFn: (id: string) => aiImages.deleteAiImage({ id }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["images"],
          });
        },
      },
      queryClient
    );
  };

  const useCreateMutation = () => {
    return useMutation(
      {
        mutationFn: (attrs: AiImageInput) =>
          aiImages.createImage({
            requestBody: { image: attrs },
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["images"],
          });
        },
      },
      queryClient
    );
  };

  return {
    useInfiniteIndexQuery,
    useIndexQuery,
    getQuery,
    useCreateMutation,
    useDeleteMutation,
    isLoading,
  };
}
